import { ROUTES } from '@/constants/routes';
import {
  useAccountActivation,
  useAccountActivationOtp,
} from '@/hooks/requests/useAccountActivation';
import {
  AccountActivationStatus,
  ActivationStatusResponse,
} from '@/types/auth';
import showToast from '@/utils/toast';
import { useNavigate, useParams } from 'react-router-dom';
import OtpForm from '@/components/OtpForm';
import useOtpForm from '@/hooks/useOtpForm';
import { OTPFormFields } from '@/types/common';
import { useEffect } from 'react';

export default function ActivationOtp() {
  const navigate = useNavigate();
  const { uuid = '' } = useParams();
  const {
    formMethods,
    handleResendError,
    handleSubmitError,
    hasFailedOnce,
    setHasFailedOnce,
    submitForm,
  } = useOtpForm();
  const { validateOtp, isLoadingValidateOtp } = useAccountActivation();
  const {
    phoneNumber,
    getAccountActivationOtp,
    isLoadingAccountActivationOtp,
  } = useAccountActivationOtp({
    uuid,
  });

  function showOtpToast(data: ActivationStatusResponse) {
    if (!data) {
      return;
    }

    if (data?.status === AccountActivationStatus.Valid) {
      showToast({
        type: 'success',
        data: { content: 'A new code has been sent' },
      });
    } else {
      showToast({
        type: 'error',
        data: {
          content:
            'An error has occurred when sending your code, our team has been notified. Please try again later.',
        },
      });
    }
  }

  useEffect(() => {
    const fetchOtp = async () => {
      const response = await getAccountActivationOtp();
      showOtpToast(response);
    };

    fetchOtp();
  }, [getAccountActivationOtp]);

  async function resendOtp() {
    try {
      const response = await getAccountActivationOtp();
      showOtpToast(response);
    } catch {
      handleResendError();
    }
  }

  async function handleSubmit(data: OTPFormFields) {
    setHasFailedOnce(true);

    try {
      const { activationStatus, setPasswordCode } = await validateOtp({
        activation_code: uuid || '',
        otp_code: data.code,
      });

      switch (activationStatus) {
        case AccountActivationStatus.Valid:
          navigate(ROUTES.ACTIVATION_SET_PASSWORD, {
            state: { setPasswordCode },
          });
          break;
        case AccountActivationStatus.Expired:
          navigate(ROUTES.ACTIVATION_EXPIRED, { replace: true });
          break;
        case AccountActivationStatus.AlreadyActivated:
          showToast({
            type: 'error',
            data: {
              content:
                'Unable to activate your account because it has already been activated.',
            },
          });
          break;
        default:
          showToast({
            type: 'error',
            data: {
              content:
                'Unable to activate your account because your activation code was invalid.',
            },
          });
      }
    } catch {
      handleSubmitError();
    }
  }

  return (
    <OtpForm
      hasFailedOnce={hasFailedOnce}
      formMethods={formMethods}
      isLoading={isLoadingValidateOtp || isLoadingAccountActivationOtp}
      isLoadingResend={isLoadingValidateOtp || isLoadingAccountActivationOtp}
      content={`A verification code has been sent to your phone number ${phoneNumber}. Please enter it below.`}
      handleResendOtp={resendOtp}
      handleSubmit={handleSubmit}
      submitForm={submitForm}
    />
  );
}
